import { useState } from 'react'
import { Link } from 'react-router-dom'
import { pageLinks } from '../../pageLinks'
import { AppShell } from '../../components/AppShell'

// ============================================================================
// Mock Data
// ============================================================================

const MOCK_STATS = {
  total: 24,
  active: 18,
  missing: 4,
  needsUpdate: 2,
}

const MOCK_COVERAGE = [
  { country: 'US', code: '🇺🇸', coverage: 95 },
  { country: 'UK', code: '🇬🇧', coverage: 88 },
  { country: 'DE', code: '🇩🇪', coverage: 83 },
  { country: 'CA', code: '🇨🇦', coverage: 79 },
  { country: 'FR', code: '🇫🇷', coverage: 75 },
  { country: 'JP', code: '🇯🇵', coverage: 71 },
  { country: 'IT', code: '🇮🇹', coverage: 67 },
  { country: 'ES', code: '🇪🇸', coverage: 63 },
  { country: 'MX', code: '🇲🇽', coverage: 50 },
  { country: 'AU', code: '🇦🇺', coverage: 42 },
  { country: 'IN', code: '🇮🇳', coverage: 33 },
]

const MOCK_ACTIVITY = [
  { id: '1', asin: 'B0CX23V2ZK', action: 'Screenshot refreshed', country: 'US', time: '2 hours ago' },
  { id: '2', asin: 'B0DFHK72R7', action: 'Note added', country: 'DE', time: '5 hours ago' },
  { id: '3', asin: 'B0CX23V2ZK', action: 'Listing added', country: 'All', time: '1 day ago' },
  { id: '4', asin: 'B0BN9TJ3KB', action: 'A+ detected', country: 'UK', time: '2 days ago' },
  { id: '5', asin: 'B0DFHK72R7', action: 'Screenshot refreshed', country: 'FR', time: '3 days ago' },
]

// ============================================================================
// Components
// ============================================================================

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white rounded-lg border border-[#E5E5E5] p-4">
      <p className="text-[12px] text-[#71717A] mb-1">{label}</p>
      <p className={`text-2xl font-semibold ${color}`}>{value}</p>
    </div>
  )
}

function CoverageChart() {
  const maxCoverage = 100
  return (
    <div className="bg-white rounded-lg border border-[#E5E5E5] p-5">
      <h3 className="text-[13px] font-semibold text-[#18181B] mb-4">A+ Coverage by Marketplace</h3>
      <div className="space-y-3">
        {MOCK_COVERAGE.map((item) => (
          <div key={item.country} className="flex items-center gap-3">
            <span className="text-sm w-6 shrink-0">{item.code}</span>
            <span className="text-[12px] text-[#52525B] w-6 shrink-0">{item.country}</span>
            <div className="flex-1 h-5 bg-[#F5F5F5] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#3B82F6] rounded-full transition-all duration-500"
                style={{ width: `${(item.coverage / maxCoverage) * 100}%` }}
              />
            </div>
            <span className="text-[12px] text-[#52525B] w-10 text-right shrink-0">{item.coverage}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function RecentActivity() {
  return (
    <div className="bg-white rounded-lg border border-[#E5E5E5] p-5">
      <h3 className="text-[13px] font-semibold text-[#18181B] mb-4">Recent Activity</h3>
      <div className="space-y-0">
        {MOCK_ACTIVITY.map((item, idx) => (
          <div key={item.id} className={`flex items-start gap-3 py-3 ${idx < MOCK_ACTIVITY.length - 1 ? 'border-b border-[#F0F0F0]' : ''}`}>
            <div className="size-2 rounded-full bg-[#3B82F6] mt-1.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-[#18181B]">
                <span className="font-medium">{item.asin}</span>
                <span className="text-[#71717A]"> — {item.action}</span>
                {item.country !== 'All' && <span className="text-[#A1A1AA]"> ({item.country})</span>}
              </p>
              <p className="text-[11px] text-[#A1A1AA] mt-0.5">{item.time}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Page
// ============================================================================

export default function AplusDashboard() {
  return (
    <AppShell activeNav="dashboard">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-lg font-semibold text-[#18181B]">A+ Content Tracker</h1>
              <p className="text-[13px] text-[#71717A] mt-0.5">Monitor your Amazon listings across all marketplaces</p>
            </div>
            <Link
              to={pageLinks.AplusListings()}
              className="px-4 h-9 bg-[#18181B] text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-[#3F3F46] transition-colors"
            >
              <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add Listing
            </Link>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <StatCard label="Total Listings" value={MOCK_STATS.total} color="text-[#18181B]" />
            <StatCard label="A+ Active" value={MOCK_STATS.active} color="text-[#16A34A]" />
            <StatCard label="A+ Missing" value={MOCK_STATS.missing} color="text-[#EF4444]" />
            <StatCard label="Needs Update" value={MOCK_STATS.needsUpdate} color="text-[#F59E0B]" />
          </div>

          {/* Coverage + Activity */}
          <div className="grid grid-cols-2 gap-4">
            <CoverageChart />
            <RecentActivity />
          </div>
        </div>
      </div>
    </AppShell>
  )
}
